// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * StringSelectControl - Select input for enum-like string settings.
 */

interface StringSelectControlProps {
  label: string;
  value: string | null;
  description?: string;
  error?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  config?: {
    allowed_values?: string[] | null;
  };
}

function formatOptionLabel(value: string): string {
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function StringSelectControl({
  label,
  value,
  description,
  error,
  onChange,
  disabled = false,
  config,
}: StringSelectControlProps) {
  const configuredOptions = config?.allowed_values?.filter(Boolean) || [];
  const options = configuredOptions.length > 0 ? configuredOptions : value ? [value] : [];
  const safeValue = value ?? options[0] ?? '';

  return (
    <div
      className="mb-4"
      data-testid={`string-control-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium">{label}</label>
        {safeValue && (
          <span className="text-sm font-bold text-purple-400" data-testid="string-value">
            {formatOptionLabel(safeValue)}
          </span>
        )}
      </div>

      <select
        value={safeValue}
        onChange={event => onChange(event.target.value)}
        disabled={disabled || options.length === 0}
        className={`w-full rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-white ${
          disabled || options.length === 0
            ? 'opacity-50 cursor-not-allowed'
            : 'cursor-pointer hover:bg-white/15'
        }`}
      >
        {options.length === 0 ? (
          <option value="">No options available</option>
        ) : (
          options.map(option => (
            <option key={option} value={option}>
              {formatOptionLabel(option)}
            </option>
          ))
        )}
      </select>

      {description && <p className="text-xs text-gray-400 mt-1">{description}</p>}

      {error && (
        <p className="text-xs text-red-400 mt-1" data-testid="string-error">
          {error}
        </p>
      )}
    </div>
  );
}
